import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Check, ChevronLeft, ChevronsRight, Loader2, Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";
import {
  TELAS,
  TOTAL_TELAS,
  PERFIS,
  STORAGE_KEY,
  ETAPAS_ANALISE,
  perfilDe,
  imagemDoPerfil,
  eixosVisiveis,
  proximaTela,
  telaAnterior,
  espelho,
  fraseEspelho,
  dadosDoLead,
  textoMicro,
  preencher,
  type ImagemQuiz,
  type Micro,
  type Pergunta,
  type Respostas,
} from "@/data/diagnostico";
import logo from "@/assets/logo-clara-camille.png";
import claraChat from "@/assets/clara-chat.jpg";
import tileMulher from "@/assets/quiz/tile-mulher.webp";
import tileHomem from "@/assets/quiz/tile-homem.webp";
import imgComunidade from "@/assets/quiz/micro-comunidade.webp";
import imgSistema from "@/assets/quiz/micro-sistema.webp";
import imgAcompanhamento from "@/assets/quiz/micro-acompanhamento.webp";
import imgSessao from "@/assets/quiz/sessao-clareza.webp";
import imgPerfilAdaptacao from "@/assets/quiz/perfil-adaptacao.webp";
import imgPerfilAdaptacaoHomem from "@/assets/quiz/perfil-adaptacao-homem.webp";
import imgPerfilRotina from "@/assets/quiz/perfil-rotina.webp";
import imgPerfilRotinaHomem from "@/assets/quiz/perfil-rotina-homem.webp";
import imgPerfilEmocional from "@/assets/quiz/perfil-emocional.webp";
import imgPerfilEmocionalHomem from "@/assets/quiz/perfil-emocional-homem.webp";
import imgPerfilRecomeco from "@/assets/quiz/perfil-recomeco.webp";
import imgPerfilRecomecoHomem from "@/assets/quiz/perfil-recomeco-homem.webp";
import imgPerfilSaude from "@/assets/quiz/perfil-saude.webp";
import imgPerfilSaudeHomem from "@/assets/quiz/perfil-saude-homem.webp";

/* ------------------------------------------------------------------ */
/* Visual do quiz: branco, Inter, preto, verde só como realce.          */
/* Pensado como um app de celular, separado da estética do site.       */
/* ------------------------------------------------------------------ */

export const Q = {
  ink: "#0B0B0F",
  muted: "#6B7280",
  body: "#3F3F46",
  line: "#E6E6EA",
  soft: "#F4F4F6",
  green: "#2E7D4F",
  greenSoft: "#E8F3EC",
  greenInk: "#1F4D32",
  red: "#D64545",
  redSoft: "#FBECEC",
} as const;

export const IMAGENS: Record<ImagemQuiz, string> = {
  "tile-mulher": tileMulher,
  "tile-homem": tileHomem,
  clara: claraChat,
  comunidade: imgComunidade,
  sistema: imgSistema,
  acompanhamento: imgAcompanhamento,
  sessao: imgSessao,
  "perfil-adaptacao": imgPerfilAdaptacao,
  "perfil-adaptacao-homem": imgPerfilAdaptacaoHomem,
  "perfil-rotina": imgPerfilRotina,
  "perfil-rotina-homem": imgPerfilRotinaHomem,
  "perfil-emocional": imgPerfilEmocional,
  "perfil-emocional-homem": imgPerfilEmocionalHomem,
  "perfil-recomeco": imgPerfilRecomeco,
  "perfil-recomeco-homem": imgPerfilRecomecoHomem,
  "perfil-saude": imgPerfilSaude,
  "perfil-saude-homem": imgPerfilSaudeHomem,
};

type Fase = "telas" | "analise" | "espelho" | "captura";

const lerSessao = (): { respostas: Respostas; indice: number } | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data && typeof data === "object" && data.respostas) return { respostas: data.respostas, indice: data.indice ?? 0 };
  } catch {
    /* sem sessão */
  }
  return null;
};

const salvarSessao = (dados: Record<string, unknown>) => {
  if (typeof window === "undefined") return;
  try {
    const atual = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...atual, ...dados }));
  } catch {
    /* armazenamento indisponível */
  }
};

/** Renderiza `**trecho**` com cor. */
export const Realce = ({ texto, cor = Q.green }: { texto: string; cor?: string }) => {
  const partes = texto.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return (
    <>
      {partes.map((p, i) =>
        p.startsWith("**") ? (
          <span key={i} style={{ color: cor }}>
            {p.slice(2, -2)}
          </span>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
};

/** Entrada escalonada (fade + sobe), com atraso em ms. */
export const Aparecer = ({ delay = 0, children, className = "" }: { delay?: number; children: ReactNode; className?: string }) => (
  <div
    className={`animate-in fade-in slide-in-from-bottom-3 duration-500 ease-out ${className}`}
    style={{ animationDelay: `${delay}ms`, animationFillMode: "both" }}
  >
    {children}
  </div>
);

/** Botão principal fixo no rodapé, com degradê branco por trás. */
export const BotaoFixo = ({
  children,
  onClick,
  tom = "ink",
  disabled,
  type = "button",
  nota,
}: {
  children: ReactNode;
  onClick?: () => void;
  tom?: "ink" | "green";
  disabled?: boolean;
  type?: "button" | "submit";
  nota?: ReactNode;
}) => (
  <div
    className="fixed inset-x-0 bottom-0 z-30 px-5 pt-8"
    style={{
      paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
      background: "linear-gradient(to top, rgba(255,255,255,1) 55%, rgba(255,255,255,0))",
    }}
  >
    <div className="mx-auto w-full max-w-md">
      <button
        type={type}
        onClick={onClick}
        disabled={disabled}
        className="flex h-14 w-full items-center justify-center rounded-full text-[16px] font-semibold text-white shadow-[0_10px_30px_-10px_rgba(0,0,0,.35)] transition-transform active:scale-[.98] disabled:opacity-40 font-sans"
        style={{ background: tom === "green" ? Q.green : Q.ink }}
      >
        {children}
      </button>
      {nota && (
        <div className="mt-2 text-center text-[11px] font-sans" style={{ color: Q.muted }}>
          {nota}
        </div>
      )}
    </div>
  </div>
);

const Progresso = ({ valor }: { valor: number }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full" style={{ background: "#ECECEF" }}>
    <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${Math.min(100, Math.max(3, valor))}%`, background: Q.ink }} />
  </div>
);

const Capa = () => (
  <div className="mb-8 space-y-4 text-center">
    <span
      className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] font-sans"
      style={{ background: Q.greenSoft, color: Q.greenInk }}
    >
      Diagnóstico gratuito · 3 minutos
    </span>
    <h1 className="text-[30px] font-extrabold leading-[1.08] tracking-[-0.02em] font-sans" style={{ color: Q.ink }}>
      Descubra o seu perfil de alimentação na Austrália
    </h1>
    <p className="mx-auto max-w-sm text-[15px] leading-relaxed font-sans" style={{ color: Q.body }}>
      E <strong style={{ color: Q.ink }}>do que você precisa</strong> para comer bem aqui.
    </p>
    <div className="flex items-center justify-center gap-2.5 pt-1">
      <img src={claraChat} alt="Clara Camille" className="h-8 w-8 rounded-full object-cover" />
      <p className="text-left text-[12px] leading-tight font-sans" style={{ color: Q.muted }}>
        Por <strong style={{ color: Q.ink }}>Clara Camille</strong>
        <br />
        Nutricionista brasileira na Austrália
      </p>
    </div>
    <p className="pt-1 text-[12px] font-medium uppercase tracking-[0.14em] font-sans" style={{ color: Q.muted }}>
      ↓ Responda para começar ↓
    </p>
  </div>
);

const IconeOpcao = ({ emoji }: { emoji?: string }) => (
  <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-[22px] leading-none" style={{ background: Q.soft }} aria-hidden>
    {emoji ?? "•"}
  </span>
);

const TelaPergunta = ({
  tela,
  respostas,
  valor,
  onEscolher,
  onContinuar,
}: {
  tela: Pergunta;
  respostas: Respostas;
  valor: string | string[] | undefined;
  onEscolher: (v: string) => void;
  onContinuar: () => void;
}) => {
  const selecionados = Array.isArray(valor) ? valor : valor ? [valor] : [];
  const layout = tela.layout ?? (tela.opcoes.some((o) => o.emoji) ? "lista" : "grade");
  const opcoes = tela.opcoes.filter((o) => !(o.soMulher && respostas.sexo === "homem"));
  const t = (x: string) => preencher(x, respostas);

  const cardBase = "transition-all duration-200 active:scale-[0.98] font-sans bg-white border";
  const sombra = "shadow-[0_1px_2px_rgba(16,24,40,.04),0_10px_24px_-14px_rgba(16,24,40,.18)]";
  const estilo = (ativo: boolean) => (ativo ? { borderColor: Q.ink, boxShadow: `0 0 0 1px ${Q.ink} inset`, background: "#FAFAFB" } : { borderColor: Q.line });

  return (
    <div key={tela.id} className="animate-in fade-in slide-in-from-right-6 duration-300 ease-out">
      <h2 className="text-center text-[26px] font-extrabold leading-[1.12] tracking-[-0.02em] font-sans" style={{ color: Q.ink }}>
        {t(tela.titulo)}
      </h2>
      {tela.subtitulo && (
        <p className="mt-2 text-center text-[14px] font-sans" style={{ color: Q.muted }}>
          {tela.subtitulo}
        </p>
      )}

      {layout === "fotos" && (
        <div className="mt-7 grid grid-cols-2 gap-3">
          {opcoes.map((o) => {
            const ativo = selecionados.includes(o.value);
            return (
              <button key={o.value} type="button" onClick={() => onEscolher(o.value)} className={`${cardBase} ${sombra} overflow-hidden rounded-[22px] text-center`} style={estilo(ativo)}>
                {o.imagem && <img src={IMAGENS[o.imagem]} alt="" className="aspect-[4/5] w-full object-cover" />}
                <span className="block px-2 py-3.5 text-[16px] font-semibold" style={{ color: Q.ink }}>
                  {t(o.label)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {layout === "grade" && (
        <div className="mt-7 grid grid-cols-2 gap-3">
          {opcoes.map((o) => {
            const ativo = selecionados.includes(o.value);
            return (
              <button key={o.value} type="button" onClick={() => onEscolher(o.value)} className={`${cardBase} ${sombra} rounded-[18px] px-3 py-4 text-center text-[15px] font-medium`} style={{ ...estilo(ativo), color: Q.ink }}>
                {t(o.label)}
              </button>
            );
          })}
        </div>
      )}

      {layout === "escala" && (
        <div className="mt-8">
          {/* 6 por linha (0 a 5, depois 6 a 10 centralizado) */}
          <div className="flex flex-wrap justify-center gap-2">
            {opcoes.map((o) => {
              const ativo = selecionados.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => onEscolher(o.value)}
                  aria-label={o.label}
                  className={`${cardBase} ${sombra} flex aspect-square w-[calc((100%-48px)/6)] items-center justify-center rounded-2xl text-[18px] font-bold tabular-nums`}
                  style={ativo ? { background: Q.ink, borderColor: Q.ink, color: "#fff" } : { borderColor: Q.line, color: Q.ink }}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
          {tela.extremos && (
            <div className="mt-4 flex justify-between px-1 text-[12px] font-medium font-sans" style={{ color: Q.muted }}>
              <span>0 = {tela.extremos[0]}</span>
              <span>10 = {tela.extremos[1]}</span>
            </div>
          )}
        </div>
      )}

      {layout === "lista" && (
        <div className="mt-7 grid gap-3">
          {opcoes.map((o) => {
            const ativo = selecionados.includes(o.value);
            return (
              <button key={o.value} type="button" onClick={() => onEscolher(o.value)} className={`${cardBase} ${sombra} flex items-center gap-3.5 rounded-[18px] px-3.5 py-3 text-left`} style={estilo(ativo)}>
                <IconeOpcao emoji={o.emoji} />
                <span className="flex-1 text-[16px] font-medium leading-snug" style={{ color: Q.ink }}>
                  {t(o.label)}
                </span>
                {tela.multi ? (
                  <span
                    className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border transition-colors"
                    style={ativo ? { background: Q.ink, borderColor: Q.ink, color: "#fff" } : { borderColor: "#CFCFD6" }}
                  >
                    {ativo && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                  </span>
                ) : (
                  ativo && <Check className="h-5 w-5 flex-shrink-0" style={{ color: Q.ink }} strokeWidth={2.5} />
                )}
              </button>
            );
          })}
        </div>
      )}

      {tela.multi && (
        <>
          <div className="h-24" />
          <BotaoFixo onClick={onContinuar} disabled={selecionados.length === 0}>
            Continuar
          </BotaoFixo>
        </>
      )}
    </div>
  );
};

const TelaMicro = ({ tela, respostas, onContinuar }: { tela: Micro; respostas: Respostas; onContinuar: () => void }) => {
  const t = textoMicro(tela, respostas);
  const eyebrow = preencher(tela.eyebrow, respostas);
  return (
    <div key={tela.id} className="pb-28">
      <Aparecer delay={0}>
        <p className="text-center text-[13px] font-medium font-sans" style={{ color: Q.muted }}>
          {eyebrow}
        </p>
      </Aparecer>
      {tela.imagem && (
        <Aparecer delay={80} className="mt-4">
          <img
            src={IMAGENS[tela.imagem]}
            alt=""
            className={`w-full rounded-[24px] object-cover shadow-[0_18px_40px_-20px_rgba(0,0,0,.35)] ${tela.imagem === "clara" ? "aspect-square object-top" : "aspect-[3/2]"}`}
          />
        </Aparecer>
      )}
      <Aparecer delay={200} className="mt-6">
        <h2 className="text-[26px] font-extrabold leading-[1.12] tracking-[-0.02em] font-sans" style={{ color: Q.ink }}>
          <Realce texto={t.titulo} />
        </h2>
      </Aparecer>
      <Aparecer delay={320} className="mt-4">
        <p className="text-[16px] leading-relaxed font-sans" style={{ color: Q.body }}>
          {t.corpo}
        </p>
      </Aparecer>
      {t.destaque && (
        <Aparecer delay={460} className="mt-5">
          <div className="flex items-start gap-3 rounded-2xl p-4" style={{ background: Q.greenSoft }}>
            {tela.assinado && <img src={claraChat} alt="Clara Camille" className="h-10 w-10 flex-shrink-0 rounded-full object-cover" />}
            <div>
              <p className="text-[15px] font-medium leading-snug font-sans" style={{ color: Q.greenInk }}>
                {t.destaque}
              </p>
              {tela.assinado && (
                <p className="mt-1 text-[12px] font-sans" style={{ color: Q.green }}>
                  Clara Camille, nutricionista
                </p>
              )}
            </div>
          </div>
        </Aparecer>
      )}
      <BotaoFixo onClick={onContinuar}>{tela.cta ?? "Continuar"}</BotaoFixo>
    </div>
  );
};

/** Análise lenta de propósito: anel de progresso + etapas que vão sendo marcadas. */
const DURACAO_ANALISE = 6800;

const TelaAnalise = ({ respostas, onFim }: { respostas: Respostas; onFim: () => void }) => {
  const [pct, setPct] = useState(0);
  const fimChamado = useRef(false);

  useEffect(() => {
    let raf = 0;
    const inicio = performance.now();
    const easeInOut = (x: number) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);
    const tick = (agora: number) => {
      const t = Math.min(1, (agora - inicio) / DURACAO_ANALISE);
      setPct(Math.round(easeInOut(t) * 100));
      if (t < 1) raf = requestAnimationFrame(tick);
      else if (!fimChamado.current) {
        fimChamado.current = true;
        setTimeout(onFim, 700);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [onFim]);

  const r = 56;
  const circ = 2 * Math.PI * r;
  const etapas = ETAPAS_ANALISE.map((e) => preencher(e, respostas));
  const limites = [22, 48, 74, 100];

  return (
    <div className="flex min-h-[calc(100dvh-140px)] flex-col items-center justify-center text-center animate-in fade-in duration-500">
      <div className="relative h-40 w-40">
        <svg viewBox="0 0 140 140" className="h-full w-full -rotate-90">
          <circle cx="70" cy="70" r={r} fill="none" stroke="#ECECEF" strokeWidth="9" />
          <circle cx="70" cy="70" r={r} fill="none" stroke={Q.green} strokeWidth="9" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[34px] font-extrabold tracking-tight font-sans tabular-nums" style={{ color: Q.ink }}>
            {pct}%
          </span>
        </div>
      </div>
      <h2 className="mt-7 text-[24px] font-extrabold leading-tight tracking-[-0.02em] font-sans" style={{ color: Q.ink }}>
        Analisando as suas respostas…
      </h2>
      <p className="mt-2 text-[14px] font-sans" style={{ color: Q.muted }}>
        Isso leva alguns segundos.
      </p>
      <ul className="mt-8 w-full max-w-sm space-y-3 text-left">
        {etapas.map((e, i) => {
          const feito = pct >= limites[i];
          const ativo = !feito && (i === 0 || pct >= limites[i - 1]);
          return (
            <li key={e} className="flex items-center gap-3 transition-opacity duration-500" style={{ opacity: feito || ativo ? 1 : 0.35 }}>
              <span
                className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border transition-colors duration-500"
                style={feito ? { background: Q.green, borderColor: Q.green, color: "#fff" } : { borderColor: "#CFCFD6" }}
              >
                {feito ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : ativo ? <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: Q.muted }} /> : null}
              </span>
              <span className="text-[15px] font-medium font-sans" style={{ color: feito ? Q.ink : Q.body }}>
                {e}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/** Barras "de onde vem o que te trava": os eixos do perfil, calculados das respostas. A primeira é o perfil. */
export const BarrasEixos = ({ respostas, animar = true }: { respostas: Respostas; animar?: boolean }) => {
  const eixos = eixosVisiveis(respostas);
  if (eixos.length === 0) return null;
  return (
    <div className="rounded-[22px] border bg-white p-5" style={{ borderColor: Q.line }}>
      <p className="text-[13px] font-semibold leading-snug font-sans" style={{ color: Q.ink }}>
        De onde vem o que te trava hoje
      </p>
      <div className="mt-4 space-y-3.5">
        {eixos.map((e, i) => (
          <div key={e.id}>
            <div className="mb-1.5 flex items-baseline justify-between gap-3 text-[13px] font-sans">
              <span className={i === 0 ? "font-bold" : "font-medium"} style={{ color: i === 0 ? Q.ink : Q.body }}>
                {e.rotulo}
              </span>
              <span className="tabular-nums font-semibold" style={{ color: i === 0 ? Q.green : Q.muted }}>
                {e.pct}%
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full" style={{ background: "#ECECEF" }}>
              <div
                className="h-full rounded-full transition-[width] duration-[1200ms] ease-out"
                style={{ width: animar ? `${Math.max(6, e.pct)}%` : "0%", background: i === 0 ? Q.green : "#B8BCC6", transitionDelay: `${i * 120}ms` }}
              />
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] font-sans" style={{ color: Q.muted }}>
        Calculado a partir das suas respostas.
      </p>
    </div>
  );
};

/** "Hoje" (a cena do perfil) → "Próximo passo" (a Sessão Clareza). */
export const HojeProximoPasso = ({ respostas }: { respostas: Respostas }) => {
  const perfil = perfilDe(respostas);
  return (
    <>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <img src={IMAGENS[imagemDoPerfil(perfil, respostas.sexo)]} alt={`Hoje: perfil ${PERFIS[perfil].nome}`} className="aspect-square w-full rounded-2xl object-cover" />
        <ChevronsRight className="h-7 w-7" style={{ color: Q.ink }} />
        <img src={IMAGENS.sessao} alt="Próximo passo: Sessão Clareza por videochamada" className="aspect-square w-full rounded-2xl object-cover" />
      </div>
      <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-2 text-center">
        <span className="rounded-xl px-2 py-2.5 text-[13px] font-semibold font-sans" style={{ background: Q.redSoft, color: Q.red }}>
          Hoje
        </span>
        <span className="w-7" />
        <span className="rounded-xl px-2 py-2.5 text-[13px] font-semibold font-sans" style={{ background: Q.greenSoft, color: Q.green }}>
          Próximo passo
        </span>
      </div>
    </>
  );
};

const TelaEspelho = ({ respostas, onContinuar }: { respostas: Respostas; onContinuar: () => void }) => {
  const perfil = PERFIS[perfilDe(respostas)];
  const cards = espelho(respostas);
  const frase = fraseEspelho(respostas);
  const homem = respostas.sexo === "homem";
  const [animar, setAnimar] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAnimar(true), 1100);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="pb-32">
      <Aparecer delay={0}>
        <p className="text-center text-[14px] font-medium font-sans" style={{ color: Q.muted }}>
          Aqui está o seu resultado…
        </p>
      </Aparecer>

      <Aparecer delay={250} className="mt-4">
        <div className="rounded-[22px] px-5 py-6 text-center text-white" style={{ background: Q.green }}>
          <p className="text-[19px] font-extrabold uppercase leading-[1.2] tracking-[-0.01em] font-sans">
            Com base nas suas respostas, você tem o perfil <span className="underline decoration-white/60 underline-offset-4">{perfil.nome}</span>
          </p>
        </div>
      </Aparecer>

      <Aparecer delay={600} className="mt-5">
        <HojeProximoPasso respostas={respostas} />
      </Aparecer>

      <Aparecer delay={950} className="mt-5">
        <BarrasEixos respostas={respostas} animar={animar} />
      </Aparecer>

      <Aparecer delay={1300} className="mt-8">
        <p className="text-[17px] font-semibold leading-snug font-sans" style={{ color: Q.ink }}>
          {perfil.manchete}
        </p>
        {frase && (
          <p className="mt-2 text-[14px] leading-relaxed font-sans" style={{ color: Q.muted }}>
            {frase}
          </p>
        )}
      </Aparecer>

      <Aparecer delay={1600} className="mt-8">
        <h3 className="text-center text-[24px] font-extrabold tracking-[-0.02em] font-sans" style={{ color: Q.ink }}>
          Você me contou que…
        </h3>
      </Aparecer>
      <div className="mt-4 grid grid-cols-2 gap-3">
        {cards.map((c, i) => (
          <Aparecer key={c.disse} delay={1800 + i * 160}>
            <div className="h-full rounded-[18px] border bg-white p-4 text-center" style={{ borderColor: Q.line }}>
              <p className="text-[15px] font-bold leading-snug font-sans" style={{ color: Q.ink }}>
                <Realce texto={c.disse} cor={c.tom === "dor" ? Q.red : Q.green} />
              </p>
              <p className="mt-2 text-[13px] leading-relaxed font-sans" style={{ color: Q.muted }}>
                {c.resposta}
              </p>
            </div>
          </Aparecer>
        ))}
      </div>

      <Aparecer delay={1800 + cards.length * 160 + 200} className="mt-8 text-center">
        <p className="text-[14px] font-sans" style={{ color: Q.muted }}>
          Por isso…
        </p>
        <div className="mt-3 rounded-[22px] px-5 py-5 text-white" style={{ background: Q.green }}>
          <p className="text-[20px] font-extrabold uppercase leading-tight font-sans">O seu perfil pede uma Sessão Clareza</p>
        </div>
        <p className="mt-3 text-[14px] leading-relaxed font-sans" style={{ color: Q.body }}>
          Toque no botão para ver o seu diagnóstico completo e o que a gente olharia {homem ? "juntos" : "juntas"} em 30 minutos.
        </p>
      </Aparecer>

      <BotaoFixo onClick={onContinuar} tom="green">
        Ver meu diagnóstico completo
      </BotaoFixo>
    </div>
  );
};

const Campo = ({ id, label, ...props }: { id: string; label: string } & React.InputHTMLAttributes<HTMLInputElement>) => (
  <label htmlFor={id} className="block">
    <span className="mb-1.5 block text-[13px] font-medium font-sans" style={{ color: Q.body }}>
      {label}
    </span>
    <input
      id={id}
      {...props}
      className="h-14 w-full rounded-2xl border bg-white px-4 text-[16px] outline-none transition-shadow font-sans placeholder:text-[#A1A1AA] focus:shadow-[0_0_0_2px_#0B0B0F]"
      style={{ borderColor: Q.line, color: Q.ink }}
    />
  </label>
);

export interface Lead {
  nome: string;
  telefone: string;
  email: string;
  instagram: string;
}

const TelaCaptura = ({ respostas, onEnviado }: { respostas: Respostas; onEnviado: (lead: Lead) => void }) => {
  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");
  const [instagram, setInstagram] = useState("");
  const [enviando, setEnviando] = useState(false);
  const { toast } = useToast();
  const perfil = PERFIS[perfilDe(respostas)];

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (nome.trim().length < 2 || telefone.trim().length < 6 || !email.includes("@")) {
      toast({ title: "Preencha nome, WhatsApp e e-mail", variant: "destructive" });
      return;
    }
    setEnviando(true);
    const arroba = instagram.trim().replace(/^@+/, "");
    const lead: Lead = { nome: nome.trim(), telefone: telefone.trim(), email: email.trim().toLowerCase(), instagram: arroba ? `@${arroba}` : "" };
    try {
      await supabase.functions.invoke("submit-questionnaire", {
        body: {
          form_type: "diagnostico",
          ...lead,
          ...dadosDoLead(respostas),
          origem: typeof window !== "undefined" ? window.location.href : "",
        },
      });
    } catch (err) {
      console.error(err);
      // Não trava a pessoa: o resultado aparece mesmo se o envio falhar.
    } finally {
      setEnviando(false);
      onEnviado(lead);
    }
  };

  return (
    <form onSubmit={enviar} className="animate-in fade-in slide-in-from-right-6 duration-300 ease-out pb-36">
      <p className="text-center text-[13px] font-medium leading-snug font-sans" style={{ color: Q.muted }}>
        Último passo antes do seu diagnóstico completo…
      </p>
      <h2 className="mt-3 text-center text-[26px] font-extrabold leading-[1.12] tracking-[-0.02em] font-sans" style={{ color: Q.ink }}>
        Para quem eu preparo o seu diagnóstico?
      </h2>
      <div className="mt-7 space-y-4">
        <Campo id="diag-nome" label="Nome" type="text" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Digite seu nome…" autoComplete="name" maxLength={100} required />
        <Campo id="diag-telefone" label="WhatsApp" type="tel" value={telefone} onChange={(e) => setTelefone(e.target.value)} placeholder="04XX XXX XXX" autoComplete="tel" maxLength={30} required />
        <Campo id="diag-email" label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Digite seu e-mail…" autoComplete="email" maxLength={120} required />
        <Campo id="diag-instagram" label="Seu @ no Instagram (opcional)" type="text" value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="@seuperfil" autoComplete="off" autoCapitalize="none" maxLength={60} />
      </div>
      <div className="mt-6 flex items-center gap-3 rounded-2xl border p-3" style={{ borderColor: Q.line }}>
        <img src={claraChat} alt="Clara Camille" className="h-14 w-14 flex-shrink-0 rounded-xl object-cover" />
        <p className="text-[13px] leading-snug font-sans" style={{ color: Q.body }}>
          O seu diagnóstico do perfil <strong style={{ color: Q.ink }}>{perfil.nome}</strong> aparece na próxima tela. As suas respostas chegam para mim: se você marcar a Sessão Clareza, eu já chego conhecendo o seu caso.
        </p>
      </div>
      <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-[11px] leading-relaxed font-sans" style={{ color: Q.muted }}>
        <Lock className="h-3 w-3 flex-shrink-0" /> Seus dados ficam só com a Clara.{" "}
        <Link to="/politica-de-privacidade" className="underline underline-offset-2">
          Privacidade
        </Link>
      </p>
      <BotaoFixo type="submit" disabled={enviando}>
        {enviando ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Enviando…
          </>
        ) : (
          "Ver meu diagnóstico"
        )}
      </BotaoFixo>
    </form>
  );
};

const Diagnostico = () => {
  const navigate = useNavigate();
  const [indice, setIndice] = useState(0);
  const [respostas, setRespostas] = useState<Respostas>({});
  const [fase, setFase] = useState<Fase>("telas");

  useEffect(() => {
    const sessao = lerSessao();
    if (sessao && Object.keys(sessao.respostas).length > 0 && sessao.indice < TELAS.length) {
      setRespostas(sessao.respostas);
      setIndice(sessao.indice);
    }
  }, []);

  const tela = TELAS[indice];
  const progresso = useMemo(() => {
    const base = fase === "telas" ? indice : fase === "analise" ? TELAS.length : fase === "espelho" ? TELAS.length + 1 : TELAS.length + 2;
    return (base / TOTAL_TELAS) * 100;
  }, [fase, indice]);

  const topo = () => {
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
  };

  const avancar = useCallback(
    (novasRespostas: Respostas) => {
      const proximo = proximaTela(indice, novasRespostas);
      if (proximo >= TELAS.length) {
        salvarSessao({ respostas: novasRespostas, indice: TELAS.length });
        trackEvent("quiz_complete", { perfil: perfilDe(novasRespostas) });
        setFase("analise");
        topo();
        return;
      }
      salvarSessao({ respostas: novasRespostas, indice: proximo });
      setIndice(proximo);
      topo();
    },
    [indice],
  );

  const escolher = (valor: string) => {
    if (!tela || tela.kind !== "pergunta") return;
    if (indice === 0 && Object.keys(respostas).length === 0) trackEvent("quiz_start");
    if (tela.multi) {
      const atual = Array.isArray(respostas[tela.id]) ? (respostas[tela.id] as string[]) : [];
      const exclusivos = tela.opcoes.filter((o) => o.exclusivo).map((o) => o.value);
      let proximo: string[];
      if (exclusivos.includes(valor)) proximo = atual.includes(valor) ? [] : [valor];
      else proximo = atual.includes(valor) ? atual.filter((v) => v !== valor) : [...atual.filter((v) => !exclusivos.includes(v)), valor];
      setRespostas({ ...respostas, [tela.id]: proximo });
      return;
    }
    const novas = { ...respostas, [tela.id]: valor };
    setRespostas(novas);
    setTimeout(() => avancar(novas), 260);
  };

  const continuar = () => avancar(respostas);

  const voltar = () => {
    if (fase !== "telas") {
      setFase("telas");
      setIndice(TELAS.length - 1);
      return;
    }
    if (indice > 0) {
      const anterior = telaAnterior(indice, respostas);
      setIndice(anterior);
      salvarSessao({ indice: anterior });
      topo();
    }
  };

  const fimAnalise = useCallback(() => {
    setFase("espelho");
    topo();
  }, []);

  const enviado = (lead: Lead) => {
    const perfil = perfilDe(respostas);
    salvarSessao({ respostas, lead, perfil, indice: TELAS.length });
    trackEvent("quiz_lead", { perfil });
    navigate({ to: "/diagnostico/resultado", search: { p: perfil } });
  };

  const ehPergunta = fase === "telas" && tela?.kind === "pergunta";
  const centralizar =
    ehPergunta && indice > 0 && ((tela as Pergunta).layout === "escala" || ((tela as Pergunta).opcoes.length <= 5 && !(tela as Pergunta).multi));

  return (
    <div className="flex min-h-screen flex-col bg-white font-sans" style={{ color: Q.ink }}>
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-14 w-full max-w-md items-center gap-3 px-4">
          {indice > 0 || fase !== "telas" ? (
            <button type="button" onClick={voltar} aria-label="Voltar" className="-ml-2 flex h-10 w-10 items-center justify-center rounded-full active:bg-[#F4F4F6]" style={{ color: Q.ink }}>
              <ChevronLeft className="h-6 w-6" />
            </button>
          ) : (
            <img src={logo} alt="Clara Camille" className="h-7 w-auto" />
          )}
          <div className="flex-1">
            <Progresso valor={progresso} />
          </div>
          <span className="w-2" />
        </div>
      </header>

      <main className={`mx-auto flex w-full max-w-md flex-1 flex-col px-5 pb-10 ${centralizar ? "justify-center pt-2" : "pt-5"}`}>
        {fase === "telas" && tela && (
          <>
            {indice === 0 && <Capa />}
            {tela.kind === "pergunta" ? (
              <TelaPergunta tela={tela} respostas={respostas} valor={respostas[tela.id]} onEscolher={escolher} onContinuar={continuar} />
            ) : (
              <TelaMicro tela={tela} respostas={respostas} onContinuar={continuar} />
            )}
          </>
        )}
        {fase === "analise" && <TelaAnalise respostas={respostas} onFim={fimAnalise} />}
        {fase === "espelho" && (
          <TelaEspelho
            respostas={respostas}
            onContinuar={() => {
              setFase("captura");
              topo();
            }}
          />
        )}
        {fase === "captura" && <TelaCaptura respostas={respostas} onEnviado={enviado} />}
      </main>

      {indice === 0 && fase === "telas" && (
        <footer className="px-5 pb-6 text-center text-[11px] font-sans" style={{ color: Q.muted }}>
          Clara Camille · Nutricionista brasileira na Austrália ·{" "}
          <Link to="/politica-de-privacidade" className="underline underline-offset-2">
            Política de privacidade
          </Link>
        </footer>
      )}
    </div>
  );
};

export default Diagnostico;
